
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '@/components/ui';
import { SubmissionSuccess } from './SubmissionSuccess';
import { PrayerFormProgress, type FormStep } from './PrayerFormProgress';
import { PrayerFormStepChoose } from './PrayerFormStepChoose';
import { PrayerFormStepShare } from './PrayerFormStepShare';
import { PrayerFormStepConnect } from './PrayerFormStepConnect';
import { PrayerType, CreatePrayerRequest, type SubmitterGender } from '@/types';
import { api } from '@/lib/api';
import {
  validatePrayerForm,
  validatePrayerFormStep2,
  validatePrayerFormStep3,
  hasValidationErrors,
  sanitizeFormData,
  type PrayerFormData,
  type ValidationErrors,
} from '@/lib/validation';
import { cn, getOrCreateDeviceId } from '@/lib/utils';

type PrayerTypeValue = 'prayer_request' | 'praise_report';

export interface WalkthroughState {
  step: FormStep;
  type: PrayerTypeValue;
}

interface PrayerFormProps {
  /** Pre-select prayer request vs answered prayer (e.g. from URL ?type=). */
  initialType?: PrayerTypeValue;
  onSuccess?: () => void;
  onWalkthroughStateChange?: (state: WalkthroughState) => void;
  className?: string;
}

const getInitialFormData = (initialType?: PrayerTypeValue): PrayerFormData => ({
  type: initialType ?? '',
  body: '',
  firstName: '',
  lastName: '',
  email: '',
  gender: '',
  phone: '',
  location: '',
  is_anonymous: false,
});

const STEP2_FIELD_ORDER: (keyof ValidationErrors)[] = ['body', 'firstName', 'lastName', 'location'];
const STEP3_FIELD_ORDER: (keyof ValidationErrors)[] = ['email', 'gender', 'phone'];

const DRAFT_STORAGE_KEY = 'one_voice_prayer_form_draft';

type PrayerFormDraft = {
  formData: PrayerFormData;
  currentStep: FormStep;
};

function readDraft(): PrayerFormDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PrayerFormDraft;
  } catch {
    return null;
  }
}

function writeDraft(draft: PrayerFormDraft): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // ignore storage quota errors
  }
}

function clearDraft(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(DRAFT_STORAGE_KEY);
}

function getInitialState(initialType?: PrayerTypeValue): PrayerFormDraft {
  if (!initialType) {
    return {
      formData: getInitialFormData(),
      currentStep: 1,
    };
  }

  const draft = readDraft();
  return {
    formData: draft?.formData ?? getInitialFormData(initialType),
    currentStep: draft?.currentStep ?? 2,
  };
}

function typeToQueryParam(type: PrayerTypeValue): string {
  return type === 'praise_report' ? 'answered_prayer' : 'prayer_request';
}

function toPrayerTypeValue(type: PrayerFormData['type']): PrayerTypeValue {
  return String(type) === 'praise_report' ? 'praise_report' : 'prayer_request';
}

function focusField(field: keyof ValidationErrors) {
  if (field === 'body') {
    document.getElementById('prayer-body')?.focus();
    return;
  }
  if (field === 'email') {
    document.getElementById('prayer-email')?.focus();
    return;
  }
  if (field === 'gender') {
    document.querySelector<HTMLInputElement>('input[name="gender"]')?.focus();
    return;
  }
  if (field === 'firstName' || field === 'lastName') {
    const inputs = document.querySelectorAll<HTMLInputElement>(
      'input[autocomplete="given-name"], input[autocomplete="family-name"]'
    );
    const target = field === 'firstName' ? inputs[0] : inputs[1];
    target?.focus();
    return;
  }
  if (field === 'location') {
    document.getElementById('prayer-location')?.focus();
    return;
  }
  if (field === 'phone') {
    document.getElementById('prayer-phone')?.focus();
  }
}

function focusFirstError(errors: ValidationErrors, fieldOrder: (keyof ValidationErrors)[]) {
  const firstField = fieldOrder.find((field) => errors[field]);
  if (firstField) focusField(firstField);
}

function StepTransition({ step, children }: { step: FormStep; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const timer = window.setTimeout(() => setVisible(true), 20);
    return () => window.clearTimeout(timer);
  }, [step]);

  return (
    <div
      className={cn(
        'transition-all duration-200 ease-out',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      )}
    >
      {children}
    </div>
  );
}

export function PrayerForm({
  initialType,
  onSuccess,
  onWalkthroughStateChange,
  className,
}: PrayerFormProps) {
  const navigate = useNavigate();
  const initialState = useRef(getInitialState(initialType));
  const [formData, setFormData] = useState<PrayerFormData>(initialState.current.formData);
  const [currentStep, setCurrentStep] = useState<FormStep>(initialState.current.currentStep);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!initialType) {
      clearDraft();
    }
  }, [initialType]);

  useEffect(() => {
    onWalkthroughStateChange?.({
      step: currentStep,
      type: toPrayerTypeValue(formData.type),
    });
  }, [currentStep, formData.type, onWalkthroughStateChange]);

  useEffect(() => {
    if (success) return;
    writeDraft({ formData, currentStep });
  }, [formData, currentStep, success]);

  const updateField = useCallback(
    <K extends keyof PrayerFormData>(field: K, value: PrayerFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof ValidationErrors]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field as keyof ValidationErrors];
          return newErrors;
        });
      }
      setSubmitError(null);
    },
    [errors]
  );

  const goToStep = useCallback(
    (step: FormStep) => {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    []
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (currentStep === 2) {
        document.getElementById('prayer-body')?.focus();
      } else if (currentStep === 3) {
        document.getElementById('prayer-email')?.focus();
      }
    }, 120);
    return () => window.clearTimeout(timer);
  }, [currentStep]);

  const handleSelectType = useCallback(
    (value: string) => {
      const type = value as PrayerFormData['type'];
      updateField('type', type);
      navigate(`/submit?type=${typeToQueryParam(toPrayerTypeValue(type))}`, { replace: true });
      goToStep(2);
    },
    [updateField, goToStep, navigate]
  );

  const handleBackToChoose = useCallback(() => {
    navigate('/submit', { replace: true });
    setFormData((prev) => ({
      ...prev,
      type: '',
    }));
    setErrors({});
    goToStep(1);
  }, [goToStep, navigate]);

  const handleContinueStep2 = useCallback(() => {
    setSubmitError(null);
    const sanitized = sanitizeFormData(formData);
    const stepErrors = validatePrayerFormStep2(sanitized);

    if (hasValidationErrors(stepErrors)) {
      setErrors(stepErrors);
      focusFirstError(stepErrors, STEP2_FIELD_ORDER);
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      for (const field of STEP2_FIELD_ORDER) {
        delete next[field];
      }
      return next;
    });
    goToStep(3);
  }, [formData, goToStep]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const sanitized = sanitizeFormData(formData);
    const step2Errors = validatePrayerFormStep2(sanitized);
    if (hasValidationErrors(step2Errors)) {
      setErrors(step2Errors);
      goToStep(2);
      focusFirstError(step2Errors, STEP2_FIELD_ORDER);
      return;
    }

    const step3Errors = validatePrayerFormStep3(sanitized);
    if (hasValidationErrors(step3Errors)) {
      setErrors(step3Errors);
      focusFirstError(step3Errors, STEP3_FIELD_ORDER);
      return;
    }

    const validationErrors = validatePrayerForm(sanitized);
    if (hasValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    let prayerType: PrayerType;
    if (String(sanitized.type) === 'prayer_request' || sanitized.type === PrayerType.PRAYER_REQUEST) {
      prayerType = PrayerType.PRAYER_REQUEST;
    } else if (String(sanitized.type) === 'praise_report' || sanitized.type === PrayerType.PRAISE_REPORT) {
      prayerType = PrayerType.PRAISE_REPORT;
    } else {
      prayerType = sanitized.type as PrayerType;
    }

    const apiData: CreatePrayerRequest = {
      type: prayerType,
      body: sanitized.body,
      firstName: sanitized.firstName || undefined,
      lastName: sanitized.lastName || undefined,
      email: sanitized.email,
      gender: sanitized.gender as SubmitterGender,
      phone: sanitized.phone || undefined,
      location: sanitized.location || undefined,
      isAnonymous: sanitized.is_anonymous,
      deviceId: getOrCreateDeviceId(),
    };

    try {
      const response = await api.post('/prayer-requests', apiData, { auth: false });

      if (!response.error) {
        clearDraft();
        setSuccess(true);
        onSuccess?.();
      } else {
        setSubmitError(response.error);
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit prayer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = useCallback(() => {
    clearDraft();
    navigate('/submit', { replace: true });
    const fresh = getInitialFormData();
    setFormData(fresh);
    setCurrentStep(1);
    setErrors({});
    setSubmitError(null);
    setSuccess(false);
    onWalkthroughStateChange?.({ step: 1, type: toPrayerTypeValue(fresh.type) });
  }, [navigate, onWalkthroughStateChange]);

  const handlePrayForOthers = useCallback(() => {
    window.location.href = '/pray';
  }, []);

  if (success) {
    return (
      <div aria-live="polite" role="status">
        <Card variant="elevated" padding="none" className={className}>
          <SubmissionSuccess
            type={toPrayerTypeValue(formData.type)}
            body={formData.body}
            onSubmitAnother={resetForm}
            onPrayForOthers={handlePrayForOthers}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      <PrayerFormProgress currentStep={currentStep} />

      <StepTransition step={currentStep}>
        {currentStep === 1 && (
          <PrayerFormStepChoose value={formData.type} onSelectType={handleSelectType} />
        )}

        {currentStep === 2 && (
          <PrayerFormStepShare
            formData={formData}
            errors={errors}
            onFieldChange={updateField}
            onBack={handleBackToChoose}
            onContinue={handleContinueStep2}
          />
        )}

        {currentStep === 3 && (
          <form onSubmit={handleSubmit} className="space-y-0" noValidate>
            <PrayerFormStepConnect
              formData={formData}
              errors={errors}
              isSubmitting={isSubmitting}
              submitError={submitError}
              onFieldChange={updateField}
              onBack={() => goToStep(2)}
            />
          </form>
        )}
      </StepTransition>
    </div>
  );
}
